import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import {
  ArrowLeftIcon,
  CreditCardIcon,
  CopyIcon,
  CheckIcon,
  AlertCircleIcon,
  RefreshCwIcon,
  QrCodeIcon,
  BanknoteIcon,
  CalendarIcon,
  UserIcon,
  FileTextIcon,
  LoaderIcon,
  FileSearchIcon,
  CreditCardIcon as CardIcon,
  ShieldIcon,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface Invoice {
  id: number;
  invoicenum: string;
  userid: number;
  date: string;
  duedate: string;
  subtotal: string;
  credit: string;
  tax: string;
  tax2: string;
  total: string;
  balance: string;
  status: string;
  paymentmethod: string;
  notes: string;
  items?: {
    item: Array<{
      id: number;
      type: string;
      relid: number;
      description: string;
      amount: string;
      taxed: number;
    }>;
  };
}

interface PaymentInfo {
  pix_code?: string;
  pix_qr_code?: string;
  pix_qr_code_url?: string;
  barcode?: string;
  payment_link?: string;
  due_date?: string;
  instructions?: string;
  total?: string;
  invoice_status?: string;
  invoice_status_translated?: string;
}

const InvoicePage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Carregando sua fatura...');
  const [loadingStep, setLoadingStep] = useState(0);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceData();
    }
  }, [invoiceId]);

  // Cycle through loading messages
  useEffect(() => {
    if (!isLoading) return;

    const messages = [
      'Estamos organizando sua fatura...',
      'Carregando informações de pagamento...',
      'Verificando dados da cobrança...',
      'Preparando detalhes da fatura...'
    ];

    const interval = setInterval(() => {
      setLoadingStep(prev => {
        const next = (prev + 1) % messages.length;
        setLoadingMessage(messages[next]);
        return next;
      });
    }, 2000); // Change message every 2 seconds

    return () => clearInterval(interval);
  }, [isLoading]);

  // Listen for payment confirmation events
  useEffect(() => {
    const handlePaymentConfirmed = (event: CustomEvent) => {
      // Set payment confirmed state
      setPaymentConfirmed(true);

      // Update invoice status to Paid
      setInvoice(prev => prev ? { ...prev, status: 'Paid' } : null);

      // Clear payment info since it's now paid
      setPaymentInfo(null);

      // Navigate to services page after a short delay
      setTimeout(() => {
        navigate('/services');
      }, 3000); // 3 second delay to show the success state
    };

    // Add event listener
    window.addEventListener('payment_confirmed', handlePaymentConfirmed as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('payment_confirmed', handlePaymentConfirmed as EventListener);
    };
  }, [navigate]);

  const fetchInvoiceData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Buscar dados da fatura
      const response = await api.get(`/invoices/${invoiceId}`);
      const invoiceData = response.data?.data?.invoice;

      if (!invoiceData) {
        setError('Fatura não encontrada');
        return;
      }

      setInvoice(invoiceData);

      // Buscar dados reais de pagamento do WHMCS
      await fetchRealPaymentData(invoiceData.id);

    } catch (err: any) {
      console.error('Erro ao carregar fatura:', err);
      setError(err.response?.data?.message || 'Erro ao carregar dados da fatura');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRealPaymentData = async (invoiceId: number) => {
    try {
      // Buscar dados reais de pagamento do backend
      const response = await api.get(`/invoices/${invoiceId}/payment-data`);
      const paymentData = response.data?.data?.payment_data;

      if (paymentData) {
        // Handle QR code image - if it's base64, add proper data URL prefix
        let pixQrCode = paymentData.pix_qr_code;
        if (paymentData.pix_qr_code && paymentData.pix_qr_code.startsWith && !paymentData.pix_qr_code.startsWith('data:') && !paymentData.pix_qr_code.startsWith('http')) {
          // Assume it's base64 encoded image data
          pixQrCode = `data:image/png;base64,${paymentData.pix_qr_code}`;
        }

        const paymentInfo: PaymentInfo = {
          pix_code: paymentData.pix_code,
          pix_qr_code: pixQrCode,
          pix_qr_code_url: paymentData.pix_qr_code_url,
          barcode: paymentData.barcode,
          payment_link: paymentData.payment_link,
          instructions: paymentData.instructions,
          due_date: invoice?.duedate
        };

        setPaymentInfo(paymentInfo);
      } else {
        // Fallback para dados básicos
        setPaymentInfo({
          payment_link: `${window.location.origin}/payment/${invoiceId}`,
          instructions: 'Clique no link para efetuar o pagamento.',
          due_date: invoice?.duedate
        });
      }
    } catch (error) {
      // Fallback em caso de erro
      setPaymentInfo({
        payment_link: `${window.location.origin}/payment/${invoiceId}`,
        instructions: 'Entre em contato com o suporte para obter informações de pagamento.',
        due_date: invoice?.duedate
      });
    }
  };


  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const refreshInvoice = async () => {
    setIsRefreshing(true);
    setLoadingMessage('Atualizando dados da fatura...');
    await fetchInvoiceData();
    setIsRefreshing(false);
    setLoadingMessage('Carregando sua fatura...');
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value || '0');
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(num);
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString || dateString === '0000-00-00' || dateString === '0000-00-00 00:00:00') {
        return 'Data não disponível';
      }
      
      // Se a data está no formato YYYY-MM-DD, criar a data corretamente
      let date: Date;
      if (dateString.includes('-') && dateString.length >= 10) {
        const [year, month, day] = dateString.split('-').map(num => parseInt(num));
        date = new Date(year, month - 1, day); // month - 1 porque Date usa 0-11 para meses
      } else {
        date = new Date(dateString);
      }
      
      // Verificar se a data é válida
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'unpaid':
        return 'bg-red-100 text-red-800';
      case 'overdue':
        return 'bg-red-100 text-red-900';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'Pago';
      case 'unpaid':
        return 'Aguardando Pagamento';
      case 'overdue':
        return 'Vencido';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const getPaymentMethodName = (method: string) => {
    switch (method) {
      case 'iugupix':
        return 'PIX';
      case 'boletobanktransfer':
        return 'Boleto Bancário';
      case 'banktransfer':
        return 'Transferência Bancária';
      case 'stripe':
        return 'Cartão de Crédito';
      case 'paypal':
        return 'PayPal';
      default:
        return method;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Main Loading Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
            {/* Animated Icons Container */}
            <div className="relative mb-8">
              <div className="flex justify-center items-center space-x-4">
                {/* Primary spinning icon */}
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-cyan-200 border-t-cyan-600 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <FileTextIcon className="w-6 h-6 text-cyan-600" />
                  </div>
                </div>

                {/* Secondary animated elements */}
                <div className="flex flex-col space-y-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-cyan-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '100ms' }}></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '250ms' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '400ms' }}></div>
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center animate-pulse">
                <ShieldIcon className="w-4 h-4 text-white" />
              </div>
              <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center animate-pulse">
                <CardIcon className="w-3 h-3 text-white" />
              </div>
            </div>

            {/* Loading Message */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Carregando Fatura
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed min-h-[3rem] flex items-center justify-center">
                {loadingMessage}
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-6">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-gradient-to-r from-cyan-500 to-blue-600 h-2 rounded-full animate-pulse"
                     style={{ width: `${((loadingStep + 1) / 4) * 100}%` }}>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Etapa {loadingStep + 1} de 4
              </p>
            </div>

            {/* Additional Info */}
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-full">
                <LoaderIcon className="w-4 h-4 animate-spin" />
                <span>Processando dados de cobrança...</span>
              </div>
            </div>
          </div>

          {/* Footer Message */}
          <div className="text-center mt-6">
            <p className="text-sm text-gray-500">
              Por favor, aguarde enquanto organizamos todas as informações da sua fatura
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erro</h3>
              <div className="mt-2 text-sm text-red-700">
                {error || 'Fatura não encontrada'}
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/services')}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                >
                  Voltar aos Serviços
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/services')}
            className="mr-4 p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Fatura #{invoice.invoicenum || invoice.id}
            </h1>
            <p className="text-sm text-gray-500">
              Emitida em {formatDate(invoice.date)}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invoice.status)}`}>
            {getStatusText(invoice.status)}
          </span>
          <button
            onClick={refreshInvoice}
            disabled={isRefreshing}
            className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
          >
            <RefreshCwIcon className={`h-5 w-5 text-gray-600 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Payment Success Message */}
      {paymentConfirmed && (
        <div className="rounded-md bg-green-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckIcon className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                🎉 Pagamento Confirmado!
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>Seu pagamento foi processado com sucesso. Você será redirecionado para a página de serviços em alguns segundos...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações da Fatura */}
        <div className="lg:col-span-2 space-y-6">
          {/* Detalhes da Fatura */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileTextIcon className="h-5 w-5 mr-2" />
              Detalhes da Fatura
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Número</label>
                <p className="mt-1 text-sm text-gray-900">#{invoice.invoicenum || invoice.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Data de Emissão</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(invoice.date)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Data de Vencimento</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(invoice.duedate)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Método de Pagamento</label>
                <p className="mt-1 text-sm text-gray-900">{getPaymentMethodName(invoice.paymentmethod)}</p>
              </div>
            </div>
          </div>

          {/* Itens da Fatura */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Itens</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoice.items?.item?.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  )) || (
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        Serviço VoIP
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(invoice.subtotal)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Totais */}
            <div className="mt-6 border-t border-gray-200 pt-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="text-gray-900">{formatCurrency(invoice.subtotal)}</span>
              </div>
              {parseFloat(invoice.tax) > 0 && (
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">Impostos:</span>
                  <span className="text-gray-900">{formatCurrency(invoice.tax)}</span>
                </div>
              )}
              {parseFloat(invoice.credit) > 0 && (
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">Crédito:</span>
                  <span className="text-green-600">-{formatCurrency(invoice.credit)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold mt-2 pt-2 border-t border-gray-200">
                <span className="text-gray-900">Total:</span>
                <span className="text-gray-900">{formatCurrency(invoice.total)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-gray-600">Saldo Devedor:</span>
                <span className="text-red-600 font-medium">{formatCurrency(invoice.balance)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Informações de Pagamento */}
        <div className="space-y-6">
          {invoice.status.toLowerCase() === 'unpaid' && paymentInfo && (
            <>
              {/* PIX */}
              {invoice.paymentmethod === 'iugupix' && paymentInfo.pix_code && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <QrCodeIcon className="h-5 w-5 mr-2 text-green-600" />
                    Pagamento PIX
                  </h2>
                  
                  {/* QR Code */}
                  <div className="text-center mb-4">
                    <img
                      src={paymentInfo.pix_qr_code || paymentInfo.pix_qr_code_url}
                      alt="QR Code PIX"
                      className="mx-auto w-48 h-48 border border-gray-200 rounded-lg"
                      onError={(e) => {
                        // Fallback to external QR code service if image fails to load
                        const target = e.target as HTMLImageElement;
                        if (paymentInfo.pix_qr_code_url && target.src !== paymentInfo.pix_qr_code_url) {
                          target.src = paymentInfo.pix_qr_code_url;
                        }
                      }}
                    />
                  </div>
                  
                  {/* Código PIX */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Código PIX (Copia e Cola)
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        value={paymentInfo.pix_code}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md text-xs font-mono bg-gray-50"
                      />
                      <button
                        onClick={() => copyToClipboard(paymentInfo.pix_code!, 'pix')}
                        className="px-3 py-2 bg-green-600 text-white rounded-r-md hover:bg-green-700 flex items-center"
                      >
                        {copiedField === 'pix' ? (
                          <CheckIcon className="h-4 w-4" />
                        ) : (
                          <CopyIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded-md">
                    <p className="font-medium text-blue-800 mb-1">Como pagar:</p>
                    <p>1. Abra o app do seu banco</p>
                    <p>2. Escolha a opção PIX</p>
                    <p>3. Escaneie o QR Code ou cole o código</p>
                    <p>4. Confirme o pagamento</p>
                  </div>
                </div>
              )}

              {/* Boleto */}
              {invoice.paymentmethod === 'boletobanktransfer' && paymentInfo.barcode && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BanknoteIcon className="h-5 w-5 mr-2 text-blue-600" />
                    Boleto Bancário
                  </h2>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Código de Barras
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        value={paymentInfo.barcode}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md text-sm font-mono bg-gray-50"
                      />
                      <button
                        onClick={() => copyToClipboard(paymentInfo.barcode!, 'barcode')}
                        className="px-3 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 flex items-center"
                      >
                        {copiedField === 'barcode' ? (
                          <CheckIcon className="h-4 w-4" />
                        ) : (
                          <CopyIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded-md">
                    <p className="font-medium text-blue-800 mb-1">Como pagar:</p>
                    <p>• Em qualquer banco ou lotérica</p>
                    <p>• No app do seu banco</p>
                    <p>• Em caixas eletrônicos</p>
                    <p>• Até a data de vencimento</p>
                  </div>
                </div>
              )}

              {/* Outros métodos */}
              {!['iugupix', 'boletobanktransfer'].includes(invoice.paymentmethod) && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CreditCardIcon className="h-5 w-5 mr-2 text-purple-600" />
                    Pagamento
                  </h2>
                  
                  <div className="text-center py-8">
                    <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">
                      Método: {getPaymentMethodName(invoice.paymentmethod)}
                    </p>
                    <button
                      onClick={() => window.open(paymentInfo.payment_link, '_blank')}
                      className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700"
                    >
                      Pagar Agora
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Informações Gerais */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2" />
              Informações
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Vencimento</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(invoice.duedate)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Valor Total</label>
                <p className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(invoice.total)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                  {getStatusText(invoice.status)}
                </span>
              </div>
            </div>
          </div>

          {/* Suporte */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Precisa de ajuda?</h3>
            <p className="text-xs text-gray-600 mb-3">
              Entre em contato conosco se tiver dúvidas sobre esta fatura.
            </p>
            <button className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-md text-sm hover:bg-gray-300">
              Falar com Suporte
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;